import { AlertCircle, Inbox, RefreshCw } from "lucide-react";

import { Button } from "@/common/ui/button";

import { RagStatsCards } from "./RagStatsCards";
import { RecentQueriesTable } from "./RecentQueriesTable";
import { useRagQuality } from "./use-rag-quality";

export function RagQualityPage(): React.ReactElement {
  const { metrics, isLoading, error, refresh } = useRagQuality();

  const hasQueries = metrics !== null && metrics.recent_queries.length > 0;

  return (
    <div data-testid="rag-quality-page" className="space-y-6">
      <div className="flex items-center justify-end">
        <Button
          data-testid="rag-refresh-button"
          variant="outline"
          size="sm"
          onClick={refresh}
          disabled={isLoading}
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <RagStatsCards stats={metrics?.stats ?? null} isLoading={isLoading} />

      {error && (
        <div
          data-testid="rag-quality-error"
          role="alert"
          className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive"
        >
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
          <Button
            data-testid="rag-retry-button"
            variant="outline"
            size="sm"
            className="ml-auto"
            onClick={refresh}
            disabled={isLoading}
          >
            Retry
          </Button>
        </div>
      )}

      {!isLoading && !error && !hasQueries && (
        <div
          data-testid="rag-quality-empty"
          className="flex flex-col items-center justify-center gap-3 py-16 text-muted-foreground"
        >
          <Inbox className="h-12 w-12" />
          <p className="text-lg font-medium">No queries yet</p>
          <p className="text-sm">
            RAG quality metrics will appear once the assistant processes queries.
          </p>
        </div>
      )}

      {hasQueries && <RecentQueriesTable queries={metrics.recent_queries} />}
    </div>
  );
}
